import type { ReactNode } from 'react';
import {
  ChipMeasureRow,
  GlassGroupSubheader,
  GlassMenuItem,
  RoundedTextField,
  getAutocompleteInputSx,
  getAutocompleteListboxSx,
  getGlassPaperSxObject,
} from './styles';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { useTheme } from '@mui/material/styles';
import type { SelectChangeEvent } from '@mui/material/Select';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import React from 'react';

interface SelectInputProps {
  disabled?: boolean;
  searchable?: boolean;
  options?: string[];
  groupedOptions?: { group: string; items: string[] }[];
  placeholder?: string;
  value: string | number | boolean | (string | number)[]; // only the value, it's no the object
  onChange?: (event: SelectChangeEvent<string>, child?: ReactNode) => void;
  label?: string;
  name?: string;
  id?: string;
  error?: boolean;
  helperText?: ReactNode;
  width?: string;
  startIcon?: React.ReactNode;
  sx?: object; // additional style from sx prop
  externalSx?: object;
  multiple?: boolean;
}

// Must match the Autocomplete tag margin (3px each side) and the search
// input min width + padding from getAutocompleteInputSx
const TAG_MARGIN = 6;
const SEARCH_INPUT_MIN_WIDTH = 28;

function SelectInput({
  disabled = false,
  searchable = false,
  options = [],
  groupedOptions,
  value,
  label,
  onChange,
  name,
  id,
  error,
  helperText,
  width = '100%',
  startIcon,
  sx,
  externalSx,
  multiple = false,
  placeholder = '',
}: SelectInputProps) {
  const theme = useTheme();

  const selectedValues = React.useMemo(() => {
    if (!value) return [];
    const allOptions = groupedOptions
      ? groupedOptions.flatMap((g) => g.items)
      : options;
    if (Array.isArray(value)) {
      return allOptions.filter((opt) => value.includes(opt));
    }
    return allOptions.filter((opt) => opt === value);
  }, [options, groupedOptions, value]);

  const hasValue = Array.isArray(value) ? value.length > 0 : !!value;
  const shouldShrink = hasValue || !!placeholder;

  const showChipSummary = searchable && multiple;
  const autocompleteRef = React.useRef<HTMLDivElement>(null);
  const chipMeasureRef = React.useRef<HTMLDivElement>(null);
  const [visibleChipCount, setVisibleChipCount] = React.useState(1);

  // Fits as many chips as the field width allows, reserving room for the
  // "+N" counter and the search input.
  React.useLayoutEffect(() => {
    if (!showChipSummary) return;
    const inputRoot = autocompleteRef.current?.querySelector<HTMLElement>(
      '.MuiAutocomplete-inputRoot'
    );
    const measure = chipMeasureRef.current;
    if (!inputRoot || !measure) return;

    const computeVisibleCount = () => {
      const style = getComputedStyle(inputRoot);
      const available =
        inputRoot.clientWidth -
        parseFloat(style.paddingLeft) -
        parseFloat(style.paddingRight) -
        SEARCH_INPUT_MIN_WIDTH;
      const measured = Array.from(measure.children) as HTMLElement[];
      const counterWidth = measured.pop()!.offsetWidth + TAG_MARGIN;
      const chipWidths = measured.map((el) => el.offsetWidth + TAG_MARGIN);

      let used = 0;
      let count = 0;
      for (let i = 0; i < chipWidths.length; i++) {
        const needsCounter = i < chipWidths.length - 1;
        if (
          used + chipWidths[i] + (needsCounter ? counterWidth : 0) >
          available
        )
          break;
        used += chipWidths[i];
        count++;
      }
      // The first chip always shows, shrinking with an ellipsis if needed
      setVisibleChipCount(Math.max(1, count));
    };

    computeVisibleCount();
    const observer = new ResizeObserver(computeVisibleCount);
    observer.observe(inputRoot);
    return () => observer.disconnect();
  }, [showChipSummary, selectedValues]);

  return (
    <FormControl fullWidth disabled={disabled} sx={{ width }}>
      {searchable ? (
        <Autocomplete
          ref={autocompleteRef}
          multiple={multiple}
          options={
            groupedOptions ? groupedOptions.flatMap((g) => g.items) : options
          }
          value={multiple ? selectedValues : selectedValues[0] || null}
          onChange={(_, newValue) => {
            if (onChange && newValue !== null) {
              onChange({
                target: { value: newValue },
              } as SelectChangeEvent<string>);
            }
          }}
          isOptionEqualToValue={(option, val) => option === val}
          renderValue={
            multiple
              ? (selected, getItemProps) => {
                  // Always render a compact summary (the chips that fit + "+N").
                  const items = selected as string[];
                  const getProps = getItemProps as (args: {
                    index: number;
                  }) => {
                    key: number;
                  } & Record<string, unknown>;
                  const hiddenCount = items.length - visibleChipCount;
                  return (
                    <>
                      {items.slice(0, visibleChipCount).map((item, index) => {
                        const { key, ...itemProps } = getProps({ index });
                        return (
                          <Chip
                            key={key}
                            size="small"
                            label={item}
                            sx={{ minWidth: 0, flexShrink: 1 }}
                            {...itemProps}
                          />
                        );
                      })}
                      {hiddenCount > 0 && (
                        <span className="MuiAutocomplete-tag">
                          +{hiddenCount}
                        </span>
                      )}
                    </>
                  );
                }
              : undefined
          }
          sx={getAutocompleteInputSx(multiple)}
          renderInput={(params) => (
            <RoundedTextField
              {...params}
              label={label}
              placeholder={hasValue ? undefined : placeholder}
              InputLabelProps={{
                ...params.InputLabelProps,
                shrink: hasValue ? true : undefined,
              }}
            />
          )}
          slotProps={{
            listbox: {
              sx: getAutocompleteListboxSx(theme),
            },
          }}
        />
      ) : (
        <RoundedTextField
          select
          disabled={disabled}
          label={label}
          value={Array.isArray(value) ? value : value || ''}
          onChange={onChange}
          name={name}
          id={id}
          error={error}
          startIcon={startIcon}
          sx={externalSx}
          InputLabelProps={{
            shrink: shouldShrink ? true : undefined,
          }}
          SelectProps={{
            displayEmpty: true,
            multiple: multiple,
            renderValue: (selected: unknown) => {
              if (Array.isArray(selected)) {
                if (selected.length === 0) return placeholder || '';
                return (selected as string[]).join(', ');
              }
              if (!selected || selected === '') {
                return placeholder || '';
              }
              return (selected as string) || '';
            },
            MenuProps: {
              PaperProps: {
                sx: sx ?? getGlassPaperSxObject(theme),
              },
            },
          }}
        >
          {groupedOptions
            ? groupedOptions.map((group) => [
                <GlassGroupSubheader key={`header-${group.group}`}>
                  {group.group}
                </GlassGroupSubheader>,
                ...group.items.map((option) => (
                  <GlassMenuItem value={option} key={option}>
                    {option}
                  </GlassMenuItem>
                )),
              ])
            : options?.map((option) => (
                <GlassMenuItem value={option} key={option}>
                  {option}
                </GlassMenuItem>
              ))}
        </RoundedTextField>
      )}

      {showChipSummary && (
        <ChipMeasureRow ref={chipMeasureRef} aria-hidden>
          {selectedValues.map((item) => (
            <Chip key={item} size="small" label={item} />
          ))}
          <span>+{selectedValues.length}</span>
        </ChipMeasureRow>
      )}

      {helperText && (
        <FormHelperText error={error}>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
}

export default SelectInput;
